import React, { useEffect, useState } from "react"
import { Box } from "@material-ui/core"
import { Helmet } from "react-helmet"
import { useParams } from "react-router-dom"
import { SongModel } from "models/SongModel"
import styles from "./songratingpage.module.scss"
import { logEvent } from "analyticsEvents"
import SongRatingSlider from "components/SongRatingSlider/SongRatingSlider"
import SimplePlayerControl from "components/SimplePlayerControl/SimplePlayerControl"
import CardContainer from "components/CardContainer/CardContainer"
import RainbowButton from "components/RainbowButton/RainbowButton"
import { getSong, getSongAlbumArt, getSongPlaybackUrl } from "firestoreutils/firestoresongs"
import { getUserProfilePic } from "firestoreutils/firestoreusers"
import { useCookies } from "react-cookie"
import {v4 as uuid } from "uuid";
import SongRatingButtons from "components/SongRatingButtons/SongRatingButtons"

interface Props {
  songId: string
}

const RATER_COOKIE_ID = 'raterCookieIdKey';
const origin = window.location.origin;
const rootDomain = origin.includes("localhost") ? "localhost:8080" : origin
const RATE_SONG_URL = `${rootDomain}/rateSongAnonymously`;

const SongRatingPage = () => {
  const { songId } = useParams<Props>()
  const [song, setSong] = useState<SongModel>()
  const [profilePicUrl, setProfilePicUrl] = useState<string>()
  const [albumArt, setAlbumArt] = useState<string>()
  const [fontsLoaded, setFontsLoaded] = useState<boolean>(false)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [hasDraggedRating, setHasDraggedRating] = useState<boolean>(false)
  const [songPlayUrl, setSongPlayUrl] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [ratingValue, setRatingValue] = useState<number>(2);
  const [usingRatingButtons] = useState<boolean>(Math.random() > 0.5);
  const [cookies, setCookie, removeCookie] = useCookies([RATER_COOKIE_ID]);

  useEffect(() => {
    logEvent('rate_song_page_loaded', {
      song_id: songId,
      rating_ui_variant: usingRatingButtons ? 'buttons' : 'slider'
    });
    if (!cookies.raterCookieIdKey) {
      setCookie(RATER_COOKIE_ID, uuid(), { path: '/', sameSite: 'lax' });
    }
    getSong(songId).then((song) => {
      if (!song) {
        console.error("No such song!");
        setError(true);
        return;
      }

      setSong(song);
      Promise.all([
        getSongAlbumArt(song),
        getSongPlaybackUrl(song.songID),
        getUserProfilePic(song.creatorID)
      ]).then(([albumArt, playUrl, creatorProfileUrl]) => {
        if (!albumArt || !playUrl) {
          setError(true);
          return;
        }
        setAlbumArt(albumArt);
        setSongPlayUrl(playUrl);
        setProfilePicUrl(creatorProfileUrl);
      });
    });
  }, [])

  useEffect(() => {
    // @ts-ignore
    document.fonts.ready.then(() => {
      setFontsLoaded(true);
    });
  }, [])

  useEffect(() => {
    if (songPlayUrl && song && albumArt && fontsLoaded) {
      setLoading(false)
    }
  }, [songPlayUrl, song, albumArt, fontsLoaded])

  const bodyContent = (!error && !loading) ?
      songMetadata(
        songPlayUrl,
        albumArt!,
        profilePicUrl || "",
        song!,
        ratingValue,
        isPlaying,
        hasDraggedRating,
        usingRatingButtons,
        setRatingValue, setIsPlaying, setHasDraggedRating) :
      <div></div>

  return (
    <>
      <Helmet>
        <title>{`Rate this mayk song` + !!song ? ` - ${song?.title}` : ""}</title>
      </Helmet>

      <CardContainer loading={loading} error={error}>
        {bodyContent}
      </CardContainer>
    </>
  )
}

const onSubmitClicked = async (songId: string, rating: number, usingRatingButtons: boolean) => {
  // TODO(Jeremy): Add rating UX as a param to all events.
  logEvent(
    'submit_rating_clicked',
    { song_id: songId, rating: rating, rating_ui_variant: usingRatingButtons ? 'buttons' : 'slider' }
  );

  const latLong = await fetchGeoLocation();
  const response = await fetch(RATE_SONG_URL, {
      method: 'POST',
      mode: 'cors',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ songId, rating, latLong: { latitude: latLong.lat, longitude: latLong.lng } })
    });
    if (!response.ok) {
      console.error(`Failed to submit: ${response.status} - ${response.statusText}`)
    }
}

const songMetadata = (
  songPlayUrl: string,
  albumArtUrl: string,
  profilePicUrl: string,
  song: SongModel,
  rating: number,
  isPlaying: boolean,
  hasDragged: boolean,
  usingRatingButtons: boolean,
  setRatingValue: (newRating: number) => void,
  setIsPlaying: (isPlaying: boolean) => void,
  setHasDragged: (isPlaying: boolean) => void
) => {
  const onRatingChanged = (value: number|null) => {
    if (value == null) {
      setHasDragged(false);
      return;
    }
    setRatingValue(value);
    if (!hasDragged) setIsPlaying(true);
    setHasDragged(true);
  }
  return (
    <div className={styles.songMetadataContainer}>
      <div className={styles.albumArtWrapper}>
        <img src={albumArtUrl} className={styles.albumArt} />
        <div className={styles.playPauseButton}>
          <SimplePlayerControl isPlaying={isPlaying}
              songId={song.songID}
              songUrl={songPlayUrl}
              onPlayPauseTapped={() => setIsPlaying(!isPlaying)} />
        </div>
        <img className={styles.maykSticker} src={process.env.PUBLIC_URL + "/mayk_sticker.png"} />
      </div>
      <Box display='flex' flexDirection='row' margin='0 32px'>
        <div className={styles.songMetadata}>
          <div className={styles.songName}>{song?.title}</div>
          <div className={styles.userName}>@{song?.artistHandle}</div>
        </div>
      </Box>
      {ratingSection(usingRatingButtons, song, rating, hasDragged, onRatingChanged, onSubmitClicked)}
    </div>
  )
}

const ratingSection = (
  usingRatingButtons: boolean,
  song: SongModel,
  rating: number,
  hasDragged: boolean,
  onRatingChanged: (newRating: number|null) => void,
  onSubmitClicked: (songId: string, rating: number, usingRatingButtons: boolean) => void
) => {
  return usingRatingButtons ?
      ratingButtonSection(song, hasDragged, onRatingChanged, onSubmitClicked) :
      ratingSliderSection(song, rating, hasDragged, onRatingChanged, onSubmitClicked);
}

const ratingSliderSection = (
  song: SongModel,
  rating: number,
  hasDragged: boolean,
  onRatingChanged: (newRating: number|null) => void,
  onSubmitClicked: (songId: string, rating: number, usingRatingButtons: boolean) => void
) => {
  return (
    <>
      <div className={styles.controlsContainer}>
        <SongRatingSlider songId={song.songID} onValueChanged={onRatingChanged}></SongRatingSlider>
      </div>
      <RainbowButton largeBottomMargin hidden={!hasDragged} onClick={() => onSubmitClicked(song.songID, rating, false)} href="https://link.mayk.it/rdYp/rateSongSubmitLink">
        Submit
      </RainbowButton>
    </>
  );
}

const ratingButtonSection = (
  song: SongModel,
  hasDragged: boolean,
  onRatingChanged: (newRating: number|null) => void,
  onSubmitClicked: (songId: string, rating: number, usingRatingButtons: boolean) => void
) => {
  const onRatingSelected = (rating: number|null) => {
    onRatingChanged(rating);
    if (rating !== null) {
      onSubmitClicked(song.songID, rating, true);
    }
  }
  return (
    <>
      <div className={styles.controlsContainer}>
        <SongRatingButtons songId={song.songID} onValueChanged={onRatingSelected}></SongRatingButtons>
      </div>
      <RainbowButton
          largeBottomMargin
          hidden={!hasDragged}
          onClick={() => logEvent(
            'download_button_clicked',
            { song_id: song.songID, rating_ui_variant: 'buttons' }
          )}
          href="https://link.mayk.it/rdYp/rateSongSubmitLink">
        Download mayk.it
      </RainbowButton>
    </>
  );
}

const fetchGeoLocation= async () => {
  const apiKey = "AIzaSyB6GSh3gm_RYnovW05Fz5ySflrRKGrPEkg"
  const geolocateResponse = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json'
    },
  });
  return (await geolocateResponse.json()).location;
}

export default SongRatingPage
